import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType, Result } from "@zxing/library";
import { Link, useNavigate } from "react-router-dom";

import { userState } from "../../interface/MainInterface";
import { HttpMainApi } from "../../interface/main-api";
import { isValidEan13 } from "../../utils/book-barcode";
import { DEFAULT_BOOK_IMAGE } from "./book-home";
import MobileMainFooter from "./MobileMainFooter";

interface Props {
  userState: userState;
}

interface ScannedBook {
  sk: string;
  title: string;
  author?: string;
  image?: string;
  isBorrowed: boolean;
  isBorrowedByCurrentUser?: boolean;
}

interface SearchBook {
  sk: string;
  title: string;
  author?: string;
  image?: string;
  isBorrowed: boolean;
}

type BookIdentity = { barcode: string; bookSk?: never } | { barcode?: never; bookSk: string };
type SearchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "results"; keyword: string; results: SearchBook[] }
  | { status: "empty" }
  | { status: "error"; message: string }
  | { status: "ledger" };

type Phase = "scanning" | "resolving" | "confirm" | "saving" | "complete";
type CompletedAction = "borrow" | "return";

const mainApi = new HttpMainApi();

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeSearchBook(value: unknown): SearchBook | null {
  if (!value || typeof value !== "object") return null;
  const rawBook = value as Record<string, unknown>;
  const sk = typeof rawBook.sk === "string" ? rawBook.sk.trim() : "";
  const title = typeof rawBook.title === "string" ? rawBook.title.trim() : "";
  if (!sk || !title) return null;

  const optionalText = (key: string) => {
    const text = rawBook[key];
    return typeof text === "string" ? text.trim() || undefined : undefined;
  };
  return {
    sk,
    title,
    author: optionalText("author"),
    image: optionalText("image"),
    isBorrowed: rawBook.isBorrowed === true,
  };
}

const MobileBookScan = ({ userState }: Props) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls>();
  const requestIdRef = useRef(createRequestId());
  const acceptedScanRef = useRef(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const isComposingRef = useRef(false);
  const searchRequestRef = useRef(0);
  const [phase, setPhase] = useState<Phase>("scanning");
  const [scanSession, setScanSession] = useState(0);
  const [book, setBook] = useState<ScannedBook | null>(null);
  const [bookIdentity, setBookIdentity] = useState<BookIdentity | null>(null);
  const [cameraError, setCameraError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [isBookNotFoundOpen, setIsBookNotFoundOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchInputError, setSearchInputError] = useState("");
  const [searchState, setSearchState] = useState<SearchState>({ status: "idle" });
  const [completedAction, setCompletedAction] = useState<CompletedAction | null>(null);

  const stopCamera = useCallback(() => {
    controlsRef.current?.stop();
    controlsRef.current = undefined;
    const stream = videoRef.current?.srcObject;
    if (typeof MediaStream !== "undefined" && stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const openBookSearchDialog = useCallback(() => {
    acceptedScanRef.current = true;
    searchRequestRef.current += 1;
    stopCamera();
    setPhase("scanning");
    setRequestError("");
    setSearchText("");
    setSearchInputError("");
    setSearchState({ status: "idle" });
    setIsBookNotFoundOpen(true);
  }, [stopCamera]);

  const resolveBarcode = useCallback(async (barcode: string) => {
    setPhase("resolving");
    setRequestError("");
    stopCamera();

    try {
      const response = await mainApi.resolve_book_barcode({ barcode, userId: userState.userId });
      const resolvedBook = response.response?.result?.book;
      if (response.code === "404" || (response.code === "200" && !resolvedBook)) {
        if (!mountedRef.current) return;
        openBookSearchDialog();
        return;
      }
      if (response.code !== "200" || !resolvedBook) throw new Error("도서 정보를 불러오지 못했습니다.");
      if (!mountedRef.current) return;
      setBook(resolvedBook as ScannedBook);
      setBookIdentity({ barcode });
      setPhase("confirm");
    } catch (error) {
      if (!mountedRef.current) return;
      acceptedScanRef.current = false;
      setPhase("scanning");
      setRequestError(error instanceof Error ? error.message : "도서 정보를 불러오지 못했습니다.");
      setScanSession((session) => session + 1);
    }
  }, [openBookSearchDialog, stopCamera, userState.userId]);

  useEffect(() => {
    mountedRef.current = true;
    acceptedScanRef.current = false;
    setCameraError("");
    const video = videoRef.current;
    if (!video) return;

    const hints = new Map<DecodeHintType, unknown>();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13]);
    const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
    let active = true;

    void reader
      .decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        },
        video,
        (result: Result | undefined, _error, callbackControls) => {
          if (!active || !result || acceptedScanRef.current) return;
          const barcode = result.getText();
          if (!isValidEan13(barcode)) return;
          acceptedScanRef.current = true;
          callbackControls?.stop();
          void resolveBarcode(barcode);
        },
      )
      .then((controls) => {
        if (!active || acceptedScanRef.current) controls.stop();
        else controlsRef.current = controls;
      })
      .catch(() => {
        if (active) {
          setCameraError("카메라를 켤 수 없어요. 브라우저 설정에서 카메라 권한을 허용한 뒤 다시 시도해 주세요.");
        }
      });

    return () => {
      active = false;
      stopCamera();
    };
  }, [resolveBarcode, scanSession, stopCamera]);

  useEffect(() => () => {
    mountedRef.current = false;
    stopCamera();
  }, [stopCamera]);

  const resetScanner = () => {
    stopCamera();
    acceptedScanRef.current = false;
    savingRef.current = false;
    requestIdRef.current = createRequestId();
    searchRequestRef.current += 1;
    setBook(null);
    setBookIdentity(null);
    setRequestError("");
    setIsBookNotFoundOpen(false);
    setSearchText("");
    setSearchInputError("");
    setSearchState({ status: "idle" });
    setCompletedAction(null);
    setPhase("scanning");
    setScanSession((session) => session + 1);
  };

  const restartScannerAfterNotFound = () => {
    searchRequestRef.current += 1;
    setIsBookNotFoundOpen(false);
    setSearchText("");
    setSearchInputError("");
    setSearchState({ status: "idle" });
    acceptedScanRef.current = false;
    setScanSession((session) => session + 1);
  };

  const handleBookSearch = async () => {
    const titleName = searchText.trim();
    if (!titleName) {
      setSearchInputError("책 이름을 입력해 주세요.");
      return;
    }

    const requestNumber = searchRequestRef.current + 1;
    searchRequestRef.current = requestNumber;
    setSearchInputError("");
    setSearchState({ status: "loading" });

    try {
      const response = await mainApi.get_book_list({ pk: "book", titleName });
      if (!mountedRef.current || searchRequestRef.current !== requestNumber) return;
      if (response.code === "400") {
        setSearchState({ status: "empty" });
        return;
      }
      if (response.code !== "200") throw new Error("검색 결과를 불러오지 못했습니다.");

      const results = Array.isArray(response.response?.result)
        ? response.response.result
          .map(normalizeSearchBook)
          .filter((item: SearchBook | null): item is SearchBook => item !== null)
        : [];
      setSearchState(results.length ? { status: "results", keyword: titleName, results } : { status: "empty" });
    } catch {
      if (!mountedRef.current || searchRequestRef.current !== requestNumber) return;
      setSearchState({
        status: "error",
        message: "도서 검색에 실패했어요. 잠시 후 다시 시도해 주세요.",
      });
    }
  };

  const handleBookSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isComposingRef.current) void handleBookSearch();
  };

  const handleSearchBookSelect = async (selectedBook: SearchBook) => {
    if (searchState.status === "loading") return;
    const requestNumber = searchRequestRef.current + 1;
    searchRequestRef.current = requestNumber;
    setSearchState({ status: "loading" });

    try {
      const response = await mainApi.resolve_book_by_sk({
        bookSk: selectedBook.sk,
        userId: userState.userId,
      });
      const resolvedBook = response.response?.result?.book;
      if (response.code !== "200" || !resolvedBook) throw new Error("선택한 도서 정보를 불러오지 못했습니다.");
      if (!mountedRef.current || searchRequestRef.current !== requestNumber) return;
      setBook(resolvedBook as ScannedBook);
      setBookIdentity({ bookSk: selectedBook.sk });
      setIsBookNotFoundOpen(false);
      setSearchState({ status: "idle" });
      setPhase("confirm");
    } catch {
      if (!mountedRef.current || searchRequestRef.current !== requestNumber) return;
      setSearchState({
        status: "error",
        message: "선택한 도서 정보를 불러오지 못했습니다.",
      });
    }
  };

  const handleSave = async () => {
    if (!book || !bookIdentity || savingRef.current) return;
    savingRef.current = true;
    setPhase("saving");
    setRequestError("");
    const action: CompletedAction = book.isBorrowed ? "return" : "borrow";
    const params = {
      ...bookIdentity,
      requestId: requestIdRef.current,
      userId: userState.userId,
      userName: userState.userName,
      userDong: userState.userDong,
      userHo: userState.userHo,
    };

    try {
      const response = action === "borrow"
        ? await mainApi.borrow_book(params)
        : await mainApi.return_book(params);
      if (response.code !== "200") {
        throw new Error(action === "borrow" ? "대여 처리에 실패했습니다." : "반납 처리에 실패했습니다.");
      }
      if (action === "return") window.dispatchEvent(new Event("book-loans-changed"));
      if (!mountedRef.current) return;
      setCompletedAction(action);
      setPhase("complete");
    } catch (error) {
      if (!mountedRef.current) return;
      savingRef.current = false;
      setPhase("confirm");
      setRequestError(error instanceof Error ? error.message : "처리 중 문제가 발생했습니다.");
    }
  };

  const isConfirmOpen = phase === "confirm" || phase === "saving";
  const isBorrowedByAnotherUser = Boolean(book?.isBorrowed && book.isBorrowedByCurrentUser === false);

  return (
    <>
    <main className="hpoint-renewal hp-book-scan-page">
      {phase === "scanning" || phase === "resolving" ? (
        <section className="hp-book-scanner" aria-labelledby="book-scanner-heading">
          <div className="hp-book-scanner__heading">
            <QrCodeScannerRoundedIcon aria-hidden="true" />
            <div>
              <h1 id="book-scanner-heading">책 뒤 바코드를 비춰주세요</h1>
            </div>
          </div>
          <Link className="hp-book-scanner__guide-link" to="/mobilebookguide">
            <HelpOutlineRoundedIcon aria-hidden="true" />
            처음 이용하시나요? 이용 방법 보기
          </Link>
          <div className="hp-book-scanner__guide" aria-label="바코드 촬영 방법">
            <div className="hp-book-scanner__guide-visual">
              <img src="/images/hpoint-renewal/icons/book-barcode-example.png" alt="" aria-hidden="true" />
            </div>
            <div className="hp-book-scanner__guide-copy">
              <strong>이 모양을 찾아주세요</strong>
            </div>
            <ul className="hp-book-scanner__guide-list">
              <li>책 뒷면에서 검은 세로줄을 찾아주세요.</li>
              <li>바코드 전체를 초록색 틀 안에 가로로 맞춰주세요.</li>
              <li>너무 가까이 대지 말고 잠시 멈춰주세요.</li>
            </ul>
            <p className="hp-book-scanner__guide-note">
              인식되면 버튼을 누르지 않아도 자동으로 다음 화면으로 이동해요.
            </p>
          </div>
          <div className="hp-book-scanner__camera">
            <video ref={videoRef} muted playsInline aria-label="책 바코드 카메라 화면" />
            <span aria-hidden="true" />
          </div>
          <p className="hp-book-scanner__ledger-notice">
            인식이 안 되면{" "}
            <button
              type="button"
              className="hp-book-scanner__ledger-badge hp-book-scanner__search-badge"
              onClick={openBookSearchDialog}
            >
              직접 검색
            </button>{" "}
            또는 <strong className="hp-book-scanner__ledger-badge">대여 장부</strong>를 이용해 주세요.
          </p>
          {cameraError ? (
            <div className="hp-book-scanner__notice" role="alert">
              <p>{cameraError}</p>
              <Button
                type="button"
                className="hp-book-scanner__retry"
                onClick={() => {
                  stopCamera();
                  acceptedScanRef.current = false;
                  setCameraError("");
                  setScanSession((session) => session + 1);
                }}
              >
                카메라 다시 켜기
              </Button>
            </div>
          ) : null}
          {requestError ? <p className="hp-book-scanner__error" role="alert">{requestError}</p> : null}
          {phase === "resolving" ? (
            <p className="hp-book-scanner__loading" role="status">
              바코드를 인식했어요. 도서 정보를 확인하고 있어요.
            </p>
          ) : null}
        </section>
      ) : null}

      <Dialog
        className="hp-book-scan-not-found-dialog"
        open={isBookNotFoundOpen}
        onClose={restartScannerAfterNotFound}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className="hp-book-scan-not-found-dialog__title">
          {searchState.status === "ledger" ? "대여 장부에 작성해 주세요" : "바코드로 책을 찾지 못했어요"}
        </DialogTitle>
        <DialogContent className="hp-book-scan-not-found-dialog__content">
          {searchState.status === "ledger" ? (
            <div className="hp-book-scan-search__ledger">
              <AutoStoriesRoundedIcon aria-hidden="true" />
              <p>
                기존 <strong className="hp-book-scanner__ledger-badge">대여장부</strong>에 작성해 주세요.
              </p>
            </div>
          ) : (
            <>
              <div className="hp-book-scan-search__guidance">
                <p>책 이름 중 <strong>'단어'</strong> 로 검색해보세요.</p>
                <div className="hp-book-scan-search__example">
                  <span>
                    예) <strong className="hp-book-scan-search__example-title">웅진모두의그림책) 친애하는 동물들</strong>
                  </span>
                  <span>
                    검색어 : <strong>동물들</strong>
                  </span>
                </div>
              </div>
              <form className="hp-book-scan-search__form" role="search" onSubmit={handleBookSearchSubmit} noValidate>
                <TextField
                  fullWidth
                  label="책 이름"
                  placeholder="예: 아몬드"
                  value={searchText}
                  disabled={searchState.status === "loading"}
                  error={Boolean(searchInputError)}
                  helperText={searchInputError || undefined}
                  FormHelperTextProps={{ role: "alert" }}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                    if (searchInputError) setSearchInputError("");
                  }}
                  onCompositionStart={() => {
                    isComposingRef.current = true;
                  }}
                  onCompositionEnd={() => {
                    isComposingRef.current = false;
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    if (!isComposingRef.current) void handleBookSearch();
                  }}
                />
                <Button
                  className="hp-button hp-button--primary"
                  type="submit"
                  disabled={searchState.status === "loading"}
                >
                  검색
                </Button>
              </form>

              {searchState.status === "loading" ? (
                <p className="hp-book-scan-search__status" role="status">책을 찾고 있어요.</p>
              ) : null}
              {searchState.status === "error" ? (
                <p className="hp-book-scan-search__error" role="alert">{searchState.message}</p>
              ) : null}
              {searchState.status === "empty" ? (
                <div className="hp-book-scan-search__empty">
                  <p>검색 결과가 없어요.</p>
                  <p>제목의 다른 단어로 한 번 더 검색해 보세요.</p>
                  <Button
                    type="button"
                    className="hp-button hp-button--secondary hp-book-scan-search__fallback-button"
                    onClick={() => setSearchState({ status: "ledger" })}
                  >
                    그래도 찾을 수 없어요
                  </Button>
                </div>
              ) : null}
              {searchState.status === "results" ? (
                <>
                  <p className="hp-book-scan-search__result-count" aria-live="polite">
                    ‘{searchState.keyword}’ 검색 결과 {searchState.results.length}권
                  </p>
                  <ul className="hp-book-result-list hp-book-scan-search__results" aria-label="검색된 도서 목록">
                    {searchState.results.map((result) => (
                      <li key={result.sk} className="hp-book-result">
                        <button
                          type="button"
                          className="hp-book-scan-search__result-button"
                          aria-label={`${result.title} 선택`}
                          onClick={() => void handleSearchBookSelect(result)}
                        >
                          <span className="hp-book-result__visual">
                            <img
                              className="hp-book-result__cover"
                              src={result.image || DEFAULT_BOOK_IMAGE}
                              alt={`${result.title} 표지`}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = DEFAULT_BOOK_IMAGE;
                              }}
                            />
                            {result.isBorrowed ? <span className="hp-book-borrowed-badge">대여중</span> : null}
                          </span>
                          <span className="hp-book-result__content">
                            <strong className="hp-book-result__title">{result.title}</strong>
                            <span className="hp-book-result__author">{result.author || "작가 정보 없음"}</span>
                            <span className="hp-book-scan-search__select-label">이 책 선택</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </DialogContent>
        {searchState.status === "ledger" ? (
          <DialogActions className="hp-book-scan-not-found-dialog__actions hp-book-scan-search__ledger-actions">
            <Button className="hp-button hp-button--secondary" onClick={() => setSearchState({ status: "idle" })}>
              다른 단어로 검색
            </Button>
            <Button className="hp-button hp-button--primary" onClick={() => navigate("/mobilemain")}>
              확인했어요
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>

      <Dialog open={isConfirmOpen} onClose={phase === "saving" ? undefined : resetScanner} fullWidth maxWidth="xs">
        <DialogTitle>
          {book?.isBorrowed
            ? (isBorrowedByAnotherUser ? "현재 대여 중인 도서입니다." : "이 책을 반납하시겠습니까?")
            : "이 책을 대여하시겠습니까?"}
        </DialogTitle>
        <DialogContent>
          {book ? (
            <div className="hp-book-scan-confirm">
              <img src={book.image || DEFAULT_BOOK_IMAGE} alt={`${book.title} 표지`} />
              <div>
                <strong>{book.title}</strong>
                <span>{book.author || "작가 정보 없음"}</span>
                <span>{userState.userDong}동 {userState.userHo}호</span>
              </div>
            </div>
          ) : null}
          {isBorrowedByAnotherUser ? (
            <p className="hp-book-scanner__notice">반납 처리 후 다시 스캔해 대여할 수 있습니다.</p>
          ) : null}
          {requestError ? <p className="hp-book-scanner__error" role="alert">{requestError}</p> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetScanner} disabled={phase === "saving"}>취소</Button>
          <Button className="hp-button hp-button--primary" onClick={() => void handleSave()} disabled={phase === "saving"}>
            {book?.isBorrowed ? (isBorrowedByAnotherUser ? "반납 처리하기" : "반납 완료하기") : "대여하기"}
          </Button>
        </DialogActions>
      </Dialog>

      {phase === "complete" && book && completedAction ? (
        <section className="hp-book-scan-complete" aria-live="polite">
          <span className="hp-book-scan-complete__icon" aria-hidden="true">✓</span>
          <h2>{completedAction === "borrow" ? "대여가 완료되었습니다" : "반납이 완료되었습니다"}</h2>
          <p><strong>{book.title}</strong> 처리가 완료되었습니다.</p>
          <div>
            <Button className="hp-button hp-button--secondary" onClick={resetScanner}>다른 책 스캔</Button>
            <Button className="hp-button hp-button--primary" onClick={() => navigate("/mobilemain")}>홈으로</Button>
          </div>
        </section>
      ) : null}
    </main>
    <MobileMainFooter userState={userState} currentItem="book-scan" />
    </>
  );
};

export default MobileBookScan;
